import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { CameraArea, beep } from './ClassCamera';
import { api, errorMessage } from '../../shared/api/client';
import type { ClassAnnotation, ClassLiveState, ClassSessionDetail } from '../../shared/api/types';
import { Alert, Button, Card } from '../../shared/ui/components';
import { useAsync } from '../../shared/ui/useAsync';
import { CitationContext, ScriptureText, type LibraryCitation } from '../bible/ScriptureText';
import { ClassReader, splitPages } from './ClassReader';

const COLORS = ['amarillo', 'verde', 'azul', 'rosa'] as const;
const REASONS = ['Participación', 'Versículo memorizado', 'Trajo su Biblia', 'Conducta'] as const;

type Event = { type: 'live' | 'annotations' | 'points' | 'joined' | 'error' | 'camera-request' | 'camera-rejected' | 'signal' | 'floor-done'; name?: string; personId?: string; points?: number; reason?: string; socketId?: string; from?: string; data?: unknown };

/** Canal de la clase: mandar mensajes de la cámara y escuchar los que llegan. */
export interface ClassChannel {
  send: (to: string, data: unknown) => void;
  onSignal: (fn: (from: string, data: unknown) => void) => () => void;
  /** Cambia cada vez que el socket se vuelve a unir (para volver a saludar a la cámara). */
  joins: number;
}

/** Tiempo real de la clase: avisos del servidor y, si el socket no está, revisión cada 15 s. */
export function useClassSocket(churchId: string, classroomId: string, sessionId: string | null, onEvent: (e: Event) => void): ClassChannel {
  const handler = useRef(onEvent);
  handler.current = onEvent;
  const wsRef = useRef<WebSocket | null>(null);
  const listeners = useRef(new Set<(from: string, data: unknown) => void>());
  const [joins, setJoins] = useState(0);
  useEffect(() => {
    if (!sessionId) return;
    let ws: WebSocket | null = null;
    let retry = 0;
    let timer: ReturnType<typeof setTimeout> | undefined;
    let stopped = false;
    let open = false;
    const poll = setInterval(() => {
      if (!open) handler.current({ type: 'live' });
    }, 15_000);
    const connect = () => {
      if (typeof WebSocket === 'undefined') return;
      ws = new WebSocket(`${window.location.protocol === 'https:' ? 'wss' : 'ws'}://${window.location.host}/api/organization/ws`);
      wsRef.current = ws;
      ws.onopen = () => {
        retry = 0;
        open = true;
        ws?.send(JSON.stringify({ type: 'join', churchId, classroomId, sessionId }));
      };
      ws.onmessage = (event) => {
        try {
          const e = JSON.parse(String(event.data)) as Event;
          if (e.type === 'signal' && e.from) listeners.current.forEach((fn) => fn(e.from!, e.data));
          else if (e.type === 'joined') setJoins((n) => n + 1);
          else handler.current(e);
        } catch {
          // Mensaje no reconocido: se ignora.
        }
      };
      ws.onclose = () => {
        open = false;
        if (stopped) return;
        retry += 1;
        timer = setTimeout(connect, Math.min(30_000, 1000 * 2 ** Math.min(retry, 5)));
      };
    };
    connect();
    return () => {
      stopped = true;
      clearInterval(poll);
      if (timer) clearTimeout(timer);
      ws?.close();
      wsRef.current = null;
    };
  }, [churchId, classroomId, sessionId]);
  const send = useCallback(
    (to: string, data: unknown) => {
      if (wsRef.current?.readyState === 1 && sessionId) wsRef.current.send(JSON.stringify({ type: 'signal', sessionId, to, data }));
    },
    [sessionId],
  );
  const onSignal = useCallback((fn: (from: string, data: unknown) => void) => {
    listeners.current.add(fn);
    return () => {
      listeners.current.delete(fn);
    };
  }, []);
  return useMemo(() => ({ send, onSignal, joins }), [send, onSignal, joins]);
}

/** Dónde cae cada subrayado en la página (si el maestro cambió el texto, se busca la frase). */
export function placeMarks(text: string, marks: ClassAnnotation[]): { start: number; end: number; a: ClassAnnotation }[] {
  const out: { start: number; end: number; a: ClassAnnotation }[] = [];
  for (const a of marks) {
    if (a.kind !== 'highlight' || !a.quote) continue;
    const start = a.start !== null && text.slice(a.start, a.end ?? a.start) === a.quote ? a.start : text.indexOf(a.quote);
    if (start < 0) continue;
    const end = start + a.quote.length;
    if (out.some((m) => start < m.end && end > m.start)) continue; // sin encimarse
    out.push({ start, end, a });
  }
  return out.sort((x, y) => x.start - y.start);
}

function MarkedText({ text, marks, onMark }: { text: string; marks: ClassAnnotation[]; onMark: (a: ClassAnnotation) => void }) {
  const placed = placeMarks(text, marks);
  const parts: ReactNode[] = [];
  let at = 0;
  for (const m of placed) {
    if (m.start > at) parts.push(<ScriptureText key={`t${at}`} text={text.slice(at, m.start)} className="" />);
    parts.push(
      <mark
        key={m.a.id}
        className={`hl hl--${m.a.color ?? 'amarillo'}${m.a.teacher ? ' hl--teacher' : ''}`}
        title={m.a.teacher ? `Maestro${m.a.note ? `: ${m.a.note}` : ''}` : (m.a.note ?? undefined)}
        onClick={() => onMark(m.a)}
      >
        <ScriptureText text={text.slice(m.start, m.end)} className="" />
        {m.a.note ? <sup aria-hidden="true">✎</sup> : null}
      </mark>,
    );
    at = m.end;
  }
  if (at < text.length) parts.push(<ScriptureText key={`t${at}`} text={text.slice(at)} className="" />);
  return <>{parts}</>;
}

type Selection = { page: number; start: number; end: number; quote: string };

/**
 * La lectura de la clase con subrayados y notas (privadas o del maestro para todos) y la clase en vivo:
 * el maestro la inicia, cambia de página, muestra imágenes o enlaces, da la palabra y da puntos; el alumno
 * levanta la mano, sigue al maestro y recibe sus puntos al instante.
 */
export function ClassLive({ churchId, s, roomBase, students }: { churchId: string; s: ClassSessionDetail; roomBase: string; students: { personId: string; name: string }[] }) {
  const base = `${roomBase}/sessions/${s.id}`;
  const teach = s.canTeach;
  const live = useAsync((signal) => api.get<ClassLiveState>(`${base}/live`, signal), [base]);
  const marks = useAsync((signal) => api.get<{ items: ClassAnnotation[] }>(`${base}/annotations`, signal).then((r) => r.items), [base]);
  const citations = useAsync((signal) => api.get<{ items: LibraryCitation[] }>(`${roomBase}/citations`, signal).then((r) => r.items).catch(() => []), [roomBase]);
  const library = { churchId, byLabel: new Map((citations.status === 'ready' ? citations.data : []).map((c) => [c.label, c])) };
  const [follow, setFollow] = useState(true);
  const [page, setPage] = useState<number | undefined>(undefined);
  const [selection, setSelection] = useState<Selection | null>(null);
  const [note, setNote] = useState('');
  const [share, setShare] = useState(true);
  const [active, setActive] = useState<ClassAnnotation | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [hiddenShown, setHiddenShown] = useState<string | null>(null);
  const pageRef = useRef<HTMLDivElement | null>(null);
  const L = live.status === 'ready' ? live.data : null;
  const meId = useRef<string | null>(null);

  const [cameraNote, setCameraNote] = useState<string | null>(null);
  const channel = useClassSocket(churchId, roomBase.split('/').pop()!, s.id, (e) => {
    if (e.type === 'camera-request' && teach) beep();
    if (e.type === 'camera-rejected') setCameraNote('El maestro no aceptó tu cámara por ahora.');
    if (e.type === 'floor-done' && teach && e.name) {
      setToast(`✋ ${e.name} terminó`);
      setTimeout(() => setToast(null), 4000);
    }
    if (e.type === 'live') live.reload();
    if (e.type === 'annotations') marks.reload();
    if (e.type === 'points' && e.personId && e.personId === meId.current && e.points) {
      setToast(`${e.points > 0 ? '+' : ''}${e.points} ${e.reason ?? ''} ${e.points > 0 ? '⭐' : ''}`.trim());
      setTimeout(() => setToast(null), 4000);
    }
  });
  // Quién soy: el alumno se ve en la lista de asistencia (solo trae la suya).
  meId.current = !teach && s.attendance[0] ? s.attendance[0].person.personId : null;

  const act = useCallback(async (fn: () => Promise<unknown>) => {
    setError(null);
    try {
      await fn();
    } catch (e) {
      setError(errorMessage(e));
    }
  }, []);

  // Selección de texto dentro de la página → barra para subrayar.
  const readSelection = (pageIndex: number) => {
    const sel = window.getSelection?.();
    const box = pageRef.current;
    if (!sel || sel.rangeCount === 0 || sel.isCollapsed || !box) return setSelection(null);
    const range = sel.getRangeAt(0);
    if (!box.contains(range.commonAncestorContainer)) return setSelection(null);
    const before = document.createRange();
    before.setStart(box, 0);
    before.setEnd(range.startContainer, range.startOffset);
    const quote = range.toString();
    if (!quote.trim()) return setSelection(null);
    const start = before.toString().length;
    setSelection({ page: pageIndex, start, end: start + quote.length, quote: quote.slice(0, 1000) });
  };

  async function saveMark(color: (typeof COLORS)[number]) {
    if (!selection) return;
    await act(async () => {
      await api.post(`${base}/annotations`, { ...selection, color, note: note.trim() || null, shared: teach && share });
      setSelection(null);
      setNote('');
      window.getSelection?.()?.removeAllRanges();
      marks.reload();
    });
  }

  const following = !teach && L?.live && follow;
  const shown = L?.live && L.shown && hiddenShown !== L.shown.id ? L.shown : null;
  const pages = splitPages(s.notes ?? '');
  const all = marks.status === 'ready' ? marks.data : [];
  const mine = all.filter((a) => a.mine && !a.teacher);
  const teacherMarks = all.filter((a) => a.teacher);
  const lost = (a: ClassAnnotation) => a.kind === 'highlight' && placeMarks(pages[a.page] ?? '', [a]).length === 0;

  return (
    <>
      {toast ? (
        <div className="points-toast" role="status">
          {toast}
        </div>
      ) : null}
      {error ? <Alert>{error}</Alert> : null}

      {teach ? (
        <TeacherLiveBar base={base} roomBase={roomBase} live={L} students={students} onChange={() => live.reload()} onShared={() => marks.reload()} act={act} />
      ) : L?.live ? (
        <div className="live-bar">
          <span className="live-dot">En vivo</span>
          <label className="check">
            <input type="checkbox" checked={follow} onChange={(e) => setFollow(e.target.checked)} /> Seguir al maestro
          </label>
          <Button variant={L.myHand ? 'primary' : 'secondary'} onClick={() => void act(() => api.put(`${base}/live/hand`, { raised: !L.myHand }).then(() => live.reload()))}>
            {L.myHand ? '✋ Bajar la mano' : '✋ Levantar la mano'}
          </Button>
        </div>
      ) : null}
      {L?.live ? (
        <CameraArea
          L={L}
          base={base}
          teach={teach}
          meId={meId.current}
          channel={channel}
          note={cameraNote}
          onNote={setCameraNote}
          act={act}
          onChange={() => live.reload()}
        />
      ) : null}
      {L?.live && L.floor && !teach && L.floor.personId === meId.current ? (
        <div className="floor-float" role="alert">
          <strong>✋ Tienes la palabra</strong>
          <Button onClick={() => void act(() => api.post(`${base}/live/floor/done`).then(() => live.reload()))}>Terminé</Button>
        </div>
      ) : L?.live && L.floor ? (
        <Alert tone="info">🎤 La palabra la tiene {L.floor.name}</Alert>
      ) : null}

      {L?.live ? <LiveTools L={L} base={base} teach={teach} act={act} onChange={() => live.reload()} /> : null}

      {s.notes ? (
        <CitationContext.Provider value={library}>
        <Card title="Lectura de la clase">
          <ClassReader
            text={s.notes}
            readingKey={s.id}
            page={following ? L!.page : page}
            onPage={(p) => {
              setPage(p);
              setSelection(null);
              if (teach && L?.live) void act(() => api.patch(`${base}/live`, { page: p }));
            }}
          >
            {(text, p) => (
              <div ref={pageRef} onMouseUp={() => readSelection(p)} onTouchEnd={() => setTimeout(() => readSelection(p), 50)} onKeyUp={() => readSelection(p)}>
                <MarkedText text={text} marks={all.filter((a) => a.page === p)} onMark={setActive} />
              </div>
            )}
          </ClassReader>
          {selection ? (
            <div className="mark-bar" role="group" aria-label="Subrayar">
              <span className="small">Subrayar «{selection.quote.length > 40 ? `${selection.quote.slice(0, 40)}…` : selection.quote}»</span>
              <div className="mark-bar__colors">
                {COLORS.map((c) => (
                  <button key={c} type="button" className={`mark-swatch hl--${c}`} aria-label={`Subrayar en ${c}`} onClick={() => void saveMark(c)} />
                ))}
              </div>
              <input aria-label="Nota (opcional)" placeholder="Nota (opcional)" value={note} maxLength={2000} onChange={(e) => setNote(e.target.value)} />
              {teach ? (
                <label className="check small">
                  <input type="checkbox" checked={share} onChange={(e) => setShare(e.target.checked)} /> Para toda la clase
                </label>
              ) : null}
              <Button variant="ghost" onClick={() => setSelection(null)}>
                Cancelar
              </Button>
            </div>
          ) : (
            <p className="small muted">Selecciona un texto para subrayarlo o escribirle una nota{teach ? ' (puedes compartirlo con toda la clase)' : ''}.</p>
          )}
        </Card>
        </CitationContext.Provider>
      ) : null}

      {active ? <AnnotationEditor a={active} base={base} canEdit={active.mine || (active.teacher && teach)} onClose={() => setActive(null)} onChange={() => marks.reload()} act={act} /> : null}

      {mine.length || teacherMarks.length ? (
        <Card title="Mis anotaciones">
          {[...teacherMarks, ...mine].map((a) => (
            <div key={a.id} className="annotation-item">
              <span className={`mark-swatch hl--${a.color ?? 'amarillo'}`} aria-hidden="true" />
              <span>
                {a.teacher ? <span className="badge">Maestro</span> : null} {a.kind === 'highlight' ? `«${a.quote}»` : a.kind === 'link' ? a.url : 'Imagen'}
                {a.note ? <span className="small block">✎ {a.note}</span> : null}
                <span className="small muted block">
                  {pages.length > 1 ? `Página ${a.page + 1}` : ''}
                  {lost(a) ? ' · texto ya no encontrado' : ''}
                </span>
              </span>
              {a.mine || (a.teacher && teach) ? (
                <Button variant="ghost" onClick={() => void act(() => api.delete(`${base}/annotations/${a.id}`).then(() => marks.reload()))} aria-label="Borrar anotación">
                  Borrar
                </Button>
              ) : null}
            </div>
          ))}
        </Card>
      ) : null}

      {shown ? <ShownModal a={shown} roomBase={roomBase} teach={teach} onClose={() => (teach ? void act(() => api.patch(`${base}/live`, { shownId: null }).then(() => live.reload())) : setHiddenShown(shown.id))} /> : null}
    </>
  );
}

function AnnotationEditor({ a, base, canEdit, onClose, onChange, act }: { a: ClassAnnotation; base: string; canEdit: boolean; onClose: () => void; onChange: () => void; act: (fn: () => Promise<unknown>) => Promise<void> }) {
  const [note, setNote] = useState(a.note ?? '');
  return (
    <Card title={a.teacher ? 'Anotación del maestro' : 'Mi anotación'} action={<Button variant="ghost" onClick={onClose}>✕</Button>}>
      <p>«{a.quote}»</p>
      {canEdit ? (
        <>
          <div className="mark-bar__colors">
            {COLORS.map((c) => (
              <button key={c} type="button" className={`mark-swatch hl--${c}${a.color === c ? ' mark-swatch--on' : ''}`} aria-label={`Color ${c}`} onClick={() => void act(() => api.patch(`${base}/annotations/${a.id}`, { color: c, note: note.trim() || null }).then(onChange))} />
            ))}
          </div>
          <textarea aria-label="Nota" rows={3} value={note} maxLength={2000} onChange={(e) => setNote(e.target.value)} />
          <div className="actions">
            <Button variant="secondary" onClick={() => void act(() => api.patch(`${base}/annotations/${a.id}`, { color: a.color, note: note.trim() || null }).then(() => { onChange(); onClose(); }))}>
              Guardar nota
            </Button>
            <Button variant="danger" onClick={() => void act(() => api.delete(`${base}/annotations/${a.id}`).then(() => { onChange(); onClose(); }))}>
              Borrar
            </Button>
          </div>
        </>
      ) : a.note ? (
        <p>✎ {a.note}</p>
      ) : null}
    </Card>
  );
}

function youtubeId(url: string): string | null {
  const m = /(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/)|youtu\.be\/)([\w-]{6,})/.exec(url);
  return m ? m[1]! : null;
}

/** Lo que el maestro muestra a todos: imagen, video de YouTube o un sitio (si no se deja ver aquí, se abre aparte). */
function ShownModal({ a, roomBase, teach, onClose }: { a: ClassAnnotation; roomBase: string; teach: boolean; onClose: () => void }) {
  const yt = a.url ? youtubeId(a.url) : null;
  return (
    <div className="scripture-modal" onClick={onClose}>
      <div className="scripture-modal__box shown-modal card" role="dialog" aria-modal="true" aria-label="Lo que muestra el maestro" onClick={(e) => e.stopPropagation()}>
        <header className="card__header">
          <h2 className="card__title">{a.kind === 'image' ? 'Imagen' : yt ? 'Video' : 'Enlace'}</h2>
          <Button variant={teach ? 'danger' : 'ghost'} onClick={onClose}>
            {teach ? 'Quitar para todos' : '✕'}
          </Button>
        </header>
        {a.kind === 'image' && a.documentId ? <img className="shown-modal__img" src={`/api${roomBase}/documents/${a.documentId}/file?ver=1`} alt={a.note ?? 'Imagen de la clase'} /> : null}
        {a.kind === 'link' && a.url ? (
          <>
            <iframe className="shown-modal__frame" src={yt ? `https://www.youtube.com/embed/${yt}` : a.url} title={a.url} allow="autoplay; encrypted-media; picture-in-picture" allowFullScreen sandbox="allow-scripts allow-same-origin allow-popups allow-presentation" />
            <p className="small muted">
              {new URL(a.url).hostname} · Si no se ve aquí, <a href={a.url} target="_blank" rel="noopener noreferrer">ábrelo en otra pestaña</a>.
            </p>
          </>
        ) : null}
        {a.note ? <p>{a.note}</p> : null}
      </div>
    </div>
  );
}

/** Controles del maestro: iniciar/terminar, mostrar imagen o enlace, manos, palabra y puntos. */
type DockPanel = 'manos' | 'enlace' | 'equipos' | 'tiempo' | 'pregunta' | 'conectados';
const PANEL_TITLES: Record<DockPanel, string> = { manos: '✋ Manos y puntos', enlace: '🔗 Mostrar un enlace', equipos: '👥 Equipos al azar', tiempo: '⏱ Temporizador', pregunta: '❓ Pregunta rápida', conectados: '🟢 Conectados ahora' };

/** Barra flotante del maestro en vivo (abajo, sigue el scroll): lo más usado a un toque; se minimiza a «🔴 En vivo». */
function TeacherLiveBar({
  base,
  roomBase,
  live: L,
  students,
  onChange,
  onShared,
  act,
}: {
  base: string;
  roomBase: string;
  live: ClassLiveState | null;
  students: { personId: string; name: string }[];
  onChange: () => void;
  onShared: () => void;
  act: (fn: () => Promise<unknown>) => Promise<void>;
}) {
  const [panel, setPanel] = useState<DockPanel | null>(null);
  const [prepared, setPrepared] = useState<{ id: string; question: string; options: string[] }[]>([]);
  // Las preguntas preparadas se piden al abrir «Pregunta».
  useEffect(() => {
    if (panel !== 'pregunta') return;
    api.get<{ items: { id: string; question: string; options: string[] }[] }>(`${base}/questions`).then((r) => setPrepared(r.items)).catch(() => setPrepared([]));
  }, [panel, base]);
  const [minimized, setMinimized] = useState(false);
  const [teams, setTeams] = useState(2);
  const [question, setQuestion] = useState('');
  const [yesNo, setYesNo] = useState(true);
  const [options, setOptions] = useState(['', '', '']);
  const [url, setUrl] = useState('');
  const [points, setPoints] = useState<{ personId: string; n: number } | null>(null);
  const [given, setGiven] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  if (!L) return null;
  if (!L.live) {
    return (
      <div className="live-bar">
        <Button onClick={() => void act(() => api.post(`${base}/live/start`).then(onChange))}>🔴 Dar la clase en vivo</Button>
        <span className="small muted">Los alumnos con la clase abierta verán tu página, tus subrayados y lo que muestres.</span>
      </div>
    );
  }
  const show = async (body: object) => {
    const a = await api.post<{ id: string }>(`${base}/annotations`, body);
    await api.patch(`${base}/live`, { shownId: a.id });
    onShared();
    onChange();
  };
  const give = (personId: string, n: number, reason: string) =>
    act(() =>
      api.post(`${roomBase}/points`, { personId, points: n, reason, sessionId: base.split('/').pop() }).then(() => {
        setPoints(null);
        setGiven(`${n > 0 ? '+' : ''}${n} · ${reason}`);
        setTimeout(() => setGiven(null), 3000);
      }),
    );
  const pointsFor = (personId: string, name: string) => (
    <span className="live-points" role="group" aria-label={`Puntos de ${name}`}>
      {[1, 5, -1].map((n) => (
        <button key={n} type="button" className={`points-btn${n < 0 ? ' points-btn--minus' : ''}${points?.personId === personId && points.n === n ? ' points-btn--on' : ''}`} onClick={() => setPoints(points?.personId === personId && points.n === n ? null : { personId, n })} aria-label={`${n > 0 ? 'Dar' : 'Quitar'} ${Math.abs(n)} a ${name}`}>
          {n > 0 ? `+${n}` : `−${-n}`}
        </button>
      ))}
    </span>
  );
  const reasonsFor = (personId: string) =>
    points?.personId === personId ? (
      <div className="panel-student__reasons">
        {REASONS.map((r) => (
          <button key={r} type="button" className="chip" onClick={() => void give(personId, points.n, r)}>
            {r}
          </button>
        ))}
      </div>
    ) : null;
  const hands = L.hands ?? [];
  const online = L.online ?? [];

  const run = (fn: () => Promise<unknown>) => act(() => fn().then(onChange));
  const toggle = (p: DockPanel) => setPanel((x) => (x === p ? null : p));
  const handsPanel = (
    <>
      <h3 className="small">✋ Manos levantadas ({hands.length})</h3>
      {hands.length === 0 ? <p className="small muted">Nadie ha levantado la mano.</p> : null}
      <ol className="live-hands">
        {hands.map((h) => (
          <li key={h.person.personId}>
            <span className="live-hands__name">{h.person.name}</span>
            <Button variant="secondary" onClick={() => void run(() => api.patch(`${base}/live`, { floorPersonId: h.person.personId }))}>
              Dar la palabra
            </Button>
            {pointsFor(h.person.personId, h.person.name)}
            {reasonsFor(h.person.personId)}
          </li>
        ))}
      </ol>
      {hands.length ? (
        <Button variant="ghost" onClick={() => void run(() => api.delete(`${base}/live/hands`))}>
          Bajar todas
        </Button>
      ) : null}
      {L.floor ? (
        <div className="live-floor">
          <span>
            🎤 Tiene la palabra: <strong>{L.floor.name}</strong>
          </span>
          {pointsFor(L.floor.personId, L.floor.name)}
          <Button variant="ghost" onClick={() => void run(() => api.patch(`${base}/live`, { floorPersonId: null }))}>
            Quitar la palabra
          </Button>
          {reasonsFor(L.floor.personId)}
        </div>
      ) : null}
      <details>
        <summary className="small">Dar puntos a cualquier alumno</summary>
        <ul className="list">
          {students.map((st) => (
            <li key={st.personId} className="list__item">
              <span>{st.name}</span>
              {pointsFor(st.personId, st.name)}
              {reasonsFor(st.personId)}
            </li>
          ))}
        </ul>
      </details>
    </>
  );

  return (
    <>
      <input
        ref={fileRef}
        type="file"
        accept="image/*"
        hidden
        aria-label="Imagen para mostrar"
        onChange={(e) => {
          const file = e.target.files?.[0];
          e.target.value = '';
          if (!file) return;
          void act(async () => {
            const doc = await api.upload<{ id: string }>(`${roomBase}/documents`, file, { 'X-Title': encodeURIComponent(file.name), 'X-Session-Id': base.split('/').pop()! });
            await show({ kind: 'image', documentId: doc.id });
          });
        }}
      />
      {/* Espacio para que la barra flotante no tape el final de la lectura. */}
      <div className="live-dock-spacer" aria-hidden="true" />
      {minimized ? (
        <button type="button" className="live-dock__fab" onClick={() => setMinimized(false)} aria-label="Abrir la barra de la clase en vivo">
          <span className="live-dot">En vivo</span>
          {hands.length ? <span className="live-dock__badge">✋ {hands.length}</span> : null}
        </button>
      ) : (
        <div className="live-dock" role="toolbar" aria-label="Clase en vivo">
          {panel ? (
            <div className="live-dock__panel">
              <div className="live-dock__panel-head">
                <strong>{PANEL_TITLES[panel]}</strong>
                <button type="button" className="live-dock__close" onClick={() => setPanel(null)} aria-label="Cerrar">
                  ✕
                </button>
              </div>
              {panel === 'manos' ? handsPanel : null}
              {panel === 'conectados' ? (
                <>
                  <p className="small muted">Quien se conecta queda presente («en línea») en la asistencia de la clase; la corriges en Asistencia.</p>
                  {online.length === 0 ? <p className="small muted">Nadie está conectado ahora.</p> : null}
                  <ul className="list">
                    {online.map((st) => (
                      <li key={st.personId} className="list__item">
                        <span>🟢 {st.name}</span>
                        {pointsFor(st.personId, st.name)}
                        {reasonsFor(st.personId)}
                      </li>
                    ))}
                  </ul>
                </>
              ) : null}
              {panel === 'enlace' ? (
                <form
                  className="verse-picker__typed"
                  onSubmit={(e) => {
                    e.preventDefault();
                    void act(async () => {
                      await show({ kind: 'link', url: url.trim() });
                      setUrl('');
                      setPanel(null);
                    });
                  }}
                >
                  <input aria-label="Enlace" type="url" placeholder="https://…" value={url} onChange={(e) => setUrl(e.target.value)} required />
                  <Button type="submit" variant="secondary">
                    Mostrar
                  </Button>
                </form>
              ) : null}
              {panel === 'equipos' ? (
                <div className="teacher-tools__row">
                  <label className="small" htmlFor="tt-teams">
                    Equipos
                  </label>
                  <select id="tt-teams" className="live-dock__select" value={teams} onChange={(e) => setTeams(Number(e.target.value))}>
                    {[2, 3, 4, 5, 6].map((n) => (
                      <option key={n} value={n}>
                        {n}
                      </option>
                    ))}
                  </select>
                  <Button variant="secondary" onClick={() => void run(() => api.put(`${base}/live/teams`, { count: teams }))}>
                    Formar
                  </Button>
                  {L.teams ? (
                    <Button variant="ghost" onClick={() => void run(() => api.put(`${base}/live/teams`, { count: null }))}>
                      Quitar equipos
                    </Button>
                  ) : null}
                </div>
              ) : null}
              {panel === 'tiempo' ? (
                <div className="teacher-tools__row" role="group" aria-label="Temporizador">
                  {[1, 3, 5, 10].map((m) => (
                    <Button key={m} variant="secondary" onClick={() => void run(() => api.put(`${base}/live/timer`, { seconds: m * 60 }))}>
                      {m} min
                    </Button>
                  ))}
                  {L.timer ? (
                    <Button variant="ghost" onClick={() => void run(() => api.put(`${base}/live/timer`, { seconds: null }))}>
                      Quitar
                    </Button>
                  ) : null}
                </div>
              ) : null}
              {panel === 'pregunta' ? (
                <>
                  {prepared.length ? (
                    <div className="live-prepared">
                      <span className="small">Preparadas:</span>
                      {prepared.map((q) => (
                        <div key={q.id} className="live-prepared__item">
                          <span>{q.question}</span>
                          <Button variant="secondary" onClick={() => void run(() => api.post(`${base}/live/poll`, { question: q.question, options: q.options })).then(() => setPanel(null))}>
                            Lanzar
                          </Button>
                        </div>
                      ))}
                    </div>
                  ) : null}
                  {L.poll ? (
                    <div className="teacher-tools__row">
                      {L.poll.open ? (
                        <Button variant="secondary" onClick={() => void run(() => api.post(`${base}/live/poll/close`, {}))}>
                          Cerrar pregunta
                        </Button>
                      ) : null}
                      <Button variant="ghost" onClick={() => void run(() => api.post(`${base}/live/poll/close`, { remove: true }))}>
                        Quitar pregunta
                      </Button>
                    </div>
                  ) : null}
                  <form
                    className="teacher-tools__poll"
                    onSubmit={(e) => {
                      e.preventDefault();
                      void run(() => api.post(`${base}/live/poll`, { question: question.trim(), options: yesNo ? ['Sí', 'No'] : options.filter((o) => o.trim()) })).then(() => {
                        setQuestion('');
                        setOptions(['', '', '']);
                        setPanel(null);
                      });
                    }}
                  >
                    <input aria-label="Pregunta" placeholder="¿…?" value={question} maxLength={300} onChange={(e) => setQuestion(e.target.value)} required />
                    <label className="check small">
                      <input type="checkbox" checked={yesNo} onChange={(e) => setYesNo(e.target.checked)} /> Sí / No
                    </label>
                    {!yesNo
                      ? options.map((o, i) => (
                          <input key={i} aria-label={`Opción ${i + 1}`} placeholder={`Opción ${i + 1}`} value={o} maxLength={120} onChange={(e) => setOptions((xs) => xs.map((x, j) => (j === i ? e.target.value : x)))} />
                        ))
                      : null}
                    {!yesNo && options.length < 6 ? (
                      <Button type="button" variant="ghost" onClick={() => setOptions((xs) => [...xs, ''])}>
                        + Opción
                      </Button>
                    ) : null}
                    <Button type="submit">Lanzar nueva</Button>
                  </form>
                </>
              ) : null}
            </div>
          ) : null}
          {given ? <p className="small live-dock__given">✓ {given}</p> : null}
          <div className="live-dock__bar">
            <span className="live-dot live-dock__dot">En vivo</span>
            <button type="button" className="live-dock__btn" onClick={() => fileRef.current?.click()}>
              <span aria-hidden="true">🖼</span> Imagen
            </button>
            <button type="button" className={`live-dock__btn${panel === 'enlace' ? ' live-dock__btn--on' : ''}`} onClick={() => toggle('enlace')}>
              <span aria-hidden="true">🔗</span> Enlace
            </button>
            {L.shown ? (
              <button type="button" className="live-dock__btn" onClick={() => void run(() => api.patch(`${base}/live`, { shownId: null }))}>
                <span aria-hidden="true">🚫</span> Quitar lo mostrado
              </button>
            ) : null}
            <button type="button" className="live-dock__btn" onClick={() => void run(() => api.post(`${base}/live/pick`))}>
              <span aria-hidden="true">🎲</span> Al azar
            </button>
            <button type="button" className={`live-dock__btn${panel === 'equipos' ? ' live-dock__btn--on' : ''}`} onClick={() => toggle('equipos')}>
              <span aria-hidden="true">👥</span> Equipos
            </button>
            <button type="button" className={`live-dock__btn${panel === 'tiempo' ? ' live-dock__btn--on' : ''}`} onClick={() => toggle('tiempo')}>
              <span aria-hidden="true">⏱</span> {L.timer ? <Countdown endsAt={L.timer.endsAt} now={L.now} /> : 'Tiempo'}
            </button>
            <button type="button" className={`live-dock__btn${panel === 'pregunta' ? ' live-dock__btn--on' : ''}`} onClick={() => toggle('pregunta')}>
              <span aria-hidden="true">❓</span> Pregunta
            </button>
            <button type="button" className={`live-dock__btn${panel === 'conectados' ? ' live-dock__btn--on' : ''}`} onClick={() => toggle('conectados')} aria-label={`Conectados (${online.length})`}>
              <span aria-hidden="true">🟢</span> Conectados
              <span className="live-dock__badge live-dock__badge--ok">{online.length}</span>
            </button>
            <button type="button" className={`live-dock__btn${panel === 'manos' ? ' live-dock__btn--on' : ''}`} onClick={() => toggle('manos')} aria-label={`Manos levantadas (${hands.length})`}>
              <span aria-hidden="true">✋</span> Manos
              {hands.length ? <span className="live-dock__badge">{hands.length}</span> : null}
            </button>
            <button type="button" className="live-dock__btn live-dock__btn--stop" onClick={() => { if (window.confirm('¿Terminar la clase en vivo para todos?')) void run(() => api.post(`${base}/live/stop`)); }}>
              <span aria-hidden="true">⏹</span> Terminar
            </button>
            <button type="button" className="live-dock__min" onClick={() => { setMinimized(true); setPanel(null); }} aria-label="Minimizar la barra">
              ▾
            </button>
          </div>
        </div>
      )}
    </>
  );
}

/** ⏱ Cuenta regresiva con la hora del servidor (todos ven lo mismo). */
export function Countdown({ endsAt, now, big = false }: { endsAt: string; now?: string; big?: boolean }) {
  const skew = useRef(now ? new Date(now).getTime() - Date.now() : 0);
  const [, tick] = useState(0);
  useEffect(() => {
    const t = setInterval(() => tick((n) => n + 1), 250);
    return () => clearInterval(t);
  }, []);
  const left = Math.max(0, Math.ceil((new Date(endsAt).getTime() - (Date.now() + skew.current)) / 1000));
  const mm = Math.floor(left / 60);
  const ss = String(left % 60).padStart(2, '0');
  return (
    <span className={`countdown${big ? ' countdown--big' : ''}${left === 0 ? ' countdown--done' : left <= 10 ? ' countdown--soon' : ''}`} role="timer" aria-live="off">
      {left === 0 ? '¡Tiempo!' : `${mm}:${ss}`}
    </span>
  );
}

/** Lo que todos ven del vivo: alumno al azar, equipos, temporizador y la pregunta rápida (con sus resultados). */
function LiveTools({ L, base, teach, act, onChange }: { L: ClassLiveState; base: string; teach: boolean; act: (fn: () => Promise<unknown>) => Promise<void>; onChange: () => void }) {
  const poll = L.poll;
  const showResults = poll && (teach || poll.myAnswer !== null || !poll.open);
  if (!L.pick && !L.teams && !L.timer && !poll) return null;
  return (
    <div className="live-tools">
      {L.timer ? (
        <div className="live-tool">
          <span className="small">⏱ Tiempo</span>
          <Countdown endsAt={L.timer.endsAt} now={L.now} big />
        </div>
      ) : null}
      {L.pick ? (
        <div className="live-tool live-tool--pick">
          <span className="small">🎲 Elegido al azar</span>
          <strong>{L.pick.name}</strong>
        </div>
      ) : null}
      {L.teams ? (
        <div className="live-tool">
          <span className="small">👥 Equipos</span>
          <ol className="live-teams">
            {L.teams.map((t, i) => (
              <li key={i}>
                <strong>Equipo {i + 1}:</strong> {t.map((p) => p.name).join(', ')}
              </li>
            ))}
          </ol>
        </div>
      ) : null}
      {poll ? (
        <div className="live-tool live-poll">
          <span className="small">❓ Pregunta {poll.open ? '' : '(cerrada)'}</span>
          <strong>{poll.question}</strong>
          <div className="live-poll__options">
            {poll.options.map((o, i) => {
              const pct = poll.total ? Math.round((poll.counts[i]! / poll.total) * 100) : 0;
              return (
                <button
                  key={i}
                  type="button"
                  className={`live-poll__option${poll.myAnswer === i ? ' live-poll__option--mine' : ''}`}
                  disabled={teach || !poll.open}
                  onClick={() => void act(() => api.put(`${base}/live/poll/answer`, { option: i }).then(onChange))}
                  style={showResults ? { backgroundSize: `${pct}% 100%` } : undefined}
                >
                  <span>{o}</span>
                  {showResults ? (
                    <span className="small">
                      {poll.counts[i]} · {pct}%
                    </span>
                  ) : null}
                </button>
              );
            })}
          </div>
          <span className="small muted">{poll.total === 1 ? '1 respuesta' : `${poll.total} respuestas`}</span>
        </div>
      ) : null}
    </div>
  );
}

